/**
 * Company Hierarchy chart — visualises the parent/child relationships
 * across Customer rows centered on the company the logged-in user
 * belongs to.
 *
 * Data source: GET /api/customer-hierarchy/tree (see
 * customers_routes.company_tree). Server returns nodes + edges and the
 * BFS level layout; this page builds a left-to-right SVG diagram with
 * the focus company highlighted.
 *
 * The layout is dependency-free on purpose — we already ship a heavy
 * frontend and a clean SVG renders fast and prints well.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Buildings, ArrowsClockwise, MagnifyingGlassPlus, MagnifyingGlassMinus,
  Target, ArrowSquareOut, WarningCircle, Crosshair,
} from "@phosphor-icons/react";
import AdminPageHeader from "@/components/AdminPageHeader";
import api from "@/lib/api";

// Tree node geometry constants — top-down layout.
const NODE_W = 200;
const NODE_H = 56;
const COL_GAP = 24;     // horizontal gap between siblings on the same level
const ROW_GAP = 60;     // vertical gap between BFS levels

/* ─── Layout helpers ────────────────────────────────────────────────
 * Top-down layout. Each BFS level is a row, siblings are packed
 * horizontally. Ancestors stack ABOVE the focus, descendants BELOW.
 *
 * Sibling order is grouped by parent so edges don't criss-cross.
 */
function computeLayout(data) {
  if (!data) return null;
  const { ancestors = [], descendants_by_level = [], center, edges = [] } = data;

  // Build rows top-to-bottom.
  const rows = [];
  ancestors.forEach((aid) => rows.push([aid]));     // ancestor chain
  rows.push([center]);                              // focus row
  descendants_by_level.forEach((lvl) => rows.push([...lvl]));
  const centerRowIdx = ancestors.length;

  // parent → children map for grouped sibling ordering.
  const parentOf = new Map();
  edges.forEach((e) => { parentOf.set(e.child, e.parent); });

  // Below the focus, group siblings by their parent so the same
  // parent's children sit together. Above the focus there is only one
  // node per row by construction.
  for (let r = centerRowIdx + 1; r < rows.length; r++) {
    const row = rows[r];
    const byParent = new Map();
    row.forEach((id) => {
      const p = parentOf.get(id);
      if (!byParent.has(p)) byParent.set(p, []);
      byParent.get(p).push(id);
    });
    // Sort parent groups by the parent's current column index so the
    // overall left-to-right order matches the row above.
    const prevRowIndex = new Map(rows[r - 1].map((id, i) => [id, i]));
    const sortedParents = [...byParent.keys()].sort((a, b) =>
      (prevRowIndex.get(a) ?? 0) - (prevRowIndex.get(b) ?? 0),
    );
    rows[r] = sortedParents.flatMap((p) => byParent.get(p));
  }

  // Pixel coords.
  const maxCols = Math.max(...rows.map((r) => r.length || 1), 1);
  const totalWidth = maxCols * (NODE_W + COL_GAP) + 40;
  const totalHeight = rows.length * (NODE_H + ROW_GAP) + 40;

  const coords = new Map();
  rows.forEach((row, rIdx) => {
    const rowWidth = row.length * NODE_W + (row.length - 1) * COL_GAP;
    const rowXOffset = (totalWidth - rowWidth) / 2;
    row.forEach((id, cIdx) => {
      coords.set(id, {
        x: rowXOffset + cIdx * (NODE_W + COL_GAP),
        y: 20 + rIdx * (NODE_H + ROW_GAP),
      });
    });
  });

  return { rows, coords, totalWidth, totalHeight, edges };
}

/* ─── Tree SVG ──────────────────────────────────────────────────── */
function NodeRect({ x, y, node, onClick, isCenter, dimmed }) {
  const stroke = isCenter ? "#dc2626" : (node?.is_dealer ? "#7c3aed" : "#475569");
  const fill = isCenter ? "#fef2f2" : (node?.is_dealer ? "#f5f3ff" : "#ffffff");
  const opacity = dimmed ? 0.45 : 1;
  return (
    <g
      data-testid={`hierarchy-node-${node?.id}`}
      transform={`translate(${x}, ${y})`}
      style={{ cursor: "pointer", opacity }}
      onClick={onClick}>
      <rect
        width={NODE_W} height={NODE_H} rx={4}
        fill={fill}
        stroke={stroke}
        strokeWidth={isCenter ? 2.5 : 1}
      />
      <text x={10} y={20} fontSize={11} fontWeight={700}
            fill="#0f172a" style={{ pointerEvents: "none" }}>
        {(node?.name || node?.id || "").slice(0, 28)}
        {(node?.name || "").length > 28 ? "…" : ""}
      </text>
      <text x={10} y={36} fontSize={9.5} fill="#475569"
            fontFamily="ui-monospace, SFMono-Regular, monospace"
            style={{ pointerEvents: "none" }}>
        {node?.id || ""}
      </text>
      <text x={10} y={50} fontSize={9}
            fill={node?.is_dealer ? "#7c3aed" : "#64748b"}
            style={{ pointerEvents: "none" }}>
        {node?.type || "—"}
        {node?.country ? ` · ${node.country}` : ""}
        {node?.compressor_owner ? " · owner" : ""}
      </text>
      {isCenter && (
        <g transform={`translate(${NODE_W - 22}, 6)`}>
          <circle r={7} fill="#dc2626" />
          <text textAnchor="middle" y={3} fontSize={9} fill="#fff" fontWeight={700}>YOU</text>
        </g>
      )}
    </g>
  );
}

function HierarchyChart({ layout, nodes, truncated, focus, onSelect, hoverId, setHoverId }) {
  if (!layout) return null;
  const { coords, totalWidth, totalHeight, edges } = layout;

  // Highlight set: edges + nodes connected to hovered node (or focus).
  const highlightId = hoverId || focus;
  const lit = new Set([highlightId]);
  edges.forEach((e) => {
    if (e.parent === highlightId) lit.add(e.child);
    if (e.child === highlightId) lit.add(e.parent);
  });

  return (
    <svg
      data-testid="hierarchy-chart"
      width={totalWidth}
      height={totalHeight}
      viewBox={`0 0 ${totalWidth} ${totalHeight}`}
      style={{ display: "block" }}>
      {/* Edges first so they sit underneath the nodes. */}
      {edges.map((e, i) => {
        const a = coords.get(e.parent);
        const b = coords.get(e.child);
        if (!a || !b) return null;
        // Top-down: parent bottom-center → child top-center.
        const x1 = a.x + NODE_W / 2;
        const y1 = a.y + NODE_H;
        const x2 = b.x + NODE_W / 2;
        const y2 = b.y;
        const my = (y1 + y2) / 2;
        const onPath = lit.has(e.parent) && lit.has(e.child);
        return (
          <path
            key={`e-${i}`}
            d={`M ${x1} ${y1} C ${x1} ${my}, ${x2} ${my}, ${x2} ${y2}`}
            stroke={onPath ? "#0f172a" : "#cbd5e1"}
            strokeWidth={onPath ? 1.5 : 1}
            fill="none"
          />
        );
      })}
      {/* "+N more" hints on leaves with truncated descendants — sit
          BELOW the node in the top-down layout. */}
      {Object.entries(truncated || {}).map(([pid, n]) => {
        const p = coords.get(pid);
        if (!p || !n) return null;
        return (
          <g key={`t-${pid}`} transform={`translate(${p.x + NODE_W / 2 - 32}, ${p.y + NODE_H + 8})`}>
            <rect width={64} height={18} rx={9} fill="#e2e8f0" stroke="#94a3b8" />
            <text x={32} y={12} textAnchor="middle" fontSize={10}
                  fill="#475569" fontWeight={700}>
              +{n} more
            </text>
          </g>
        );
      })}
      {/* Nodes. */}
      {[...coords.entries()].map(([id, pos]) => (
        <g key={id}
           onMouseEnter={() => setHoverId(id)}
           onMouseLeave={() => setHoverId(null)}>
          <NodeRect
            x={pos.x}
            y={pos.y}
            node={nodes[id] || { id, name: id }}
            isCenter={id === focus}
            dimmed={hoverId && !lit.has(id)}
            onClick={() => onSelect(id)}
          />
        </g>
      ))}
    </svg>
  );
}

/* ─── Page shell ──────────────────────────────────────────────── */
export default function CompanyHierarchyPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCenter = searchParams.get("center") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [focus, setFocus] = useState(null);   // CustomerID being centered
  const [downDepth, setDownDepth] = useState(3);
  const [upDepth, setUpDepth] = useState(6);
  const [zoom, setZoom] = useState(1);
  const [hoverId, setHoverId] = useState(null);

  // Scroll container — used by Scroll-to-me and drag-to-pan.
  const scrollRef = useRef(null);
  // Drag-to-pan state. We track on the DOM directly (not in React
  // state) so we don't trigger a re-render on every pointermove.
  const dragState = useRef({ active: false, sx: 0, sy: 0, scrollX: 0, scrollY: 0, moved: false });

  const load = async (center) => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/customer-hierarchy/tree", {
        params: {
          ...(center ? { center } : {}),
          down_depth: downDepth,
          up_depth: upDepth,
        },
      });
      setData(r.data);
      setFocus(r.data?.center || null);
      // Keep the URL's ?center= in sync so the view is shareable.
      if (r.data?.center && r.data.center !== urlCenter) {
        setSearchParams({ center: r.data.center }, { replace: true });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load company tree");
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  // Initial load (and reload when depth controls change). The URL's
  // ?center= seeds the initial focus so deep-links land on the right
  // node.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(urlCenter || null); }, [downDepth, upDepth]);

  const layout = useMemo(() => computeLayout(data), [data]);

  // Scroll the focus node into the centre of the visible viewport.
  // Used by "Scroll to me" and run automatically after each load so a
  // wide tree doesn't dump the user at (0, 0) where the focus is off
  // screen.
  const scrollToFocus = () => {
    const el = scrollRef.current;
    if (!el || !layout || !focus) return;
    const pos = layout.coords.get(focus);
    if (!pos) return;
    const targetX = (pos.x + NODE_W / 2) * zoom - el.clientWidth / 2;
    const targetY = (pos.y + NODE_H / 2) * zoom - el.clientHeight / 2;
    el.scrollTo({
      left: Math.max(0, targetX),
      top: Math.max(0, targetY),
      behavior: "smooth",
    });
  };
  // Auto-scroll once after every successful load.
  useEffect(() => {
    if (!loading && data && layout) {
      // Defer one frame so the SVG has been measured.
      requestAnimationFrame(scrollToFocus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, layout, zoom]);

  const selectedNode = focus && data?.nodes?.[focus];
  const onRecenter = (cid) => {
    if (!cid || cid === data?.center) return;
    load(cid);
  };

  // ─── Drag-to-pan handlers. We use pointer events on the scroll
  // container and adjust scrollLeft/scrollTop directly. A small "moved"
  // threshold suppresses node-click events when the user was actually
  // panning rather than clicking.
  const onPointerDown = (e) => {
    // Only left-button drags. Let scroll-bars work normally.
    if (e.button !== 0) return;
    const el = scrollRef.current;
    if (!el) return;
    dragState.current = {
      active: true,
      sx: e.clientX,
      sy: e.clientY,
      scrollX: el.scrollLeft,
      scrollY: el.scrollTop,
      moved: false,
    };
    el.setPointerCapture(e.pointerId);
    el.style.cursor = "grabbing";
  };
  const onPointerMove = (e) => {
    const s = dragState.current;
    if (!s.active) return;
    const el = scrollRef.current;
    if (!el) return;
    const dx = e.clientX - s.sx;
    const dy = e.clientY - s.sy;
    if (!s.moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
      s.moved = true;
    }
    el.scrollLeft = s.scrollX - dx;
    el.scrollTop = s.scrollY - dy;
  };
  const onPointerUp = (e) => {
    const s = dragState.current;
    if (!s.active) return;
    s.active = false;
    const el = scrollRef.current;
    if (el) {
      try { el.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      el.style.cursor = "grab";
    }
  };
  // Swallow node clicks that were actually pans.
  const handleSelect = (cid) => {
    if (dragState.current.moved) {
      dragState.current.moved = false;
      return;
    }
    onRecenter(cid);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminPageHeader
        icon={<Buildings weight="fill" />}
        title="Company hierarchy"
        subtitle="Tree of parent/child relationships centered on your company"
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />

      <div className="mx-auto max-w-[1400px] px-3 py-3 space-y-3">

        {/* Controls */}
        <div className="bg-white border border-slate-200 rounded-sm px-3 py-2 flex flex-wrap items-center gap-2">
          <button
            onClick={() => { setSearchParams({}, { replace: true }); load(null); }}
            disabled={loading}
            data-testid="hierarchy-center-on-me"
            className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-indigo-700 hover:bg-indigo-800 text-white inline-flex items-center gap-1 disabled:opacity-50">
            <Target size={11} weight="bold" /> Center on me
          </button>
          <button
            onClick={scrollToFocus}
            disabled={loading || !focus}
            data-testid="hierarchy-scroll-to-me"
            title="Scroll the viewport to the highlighted node"
            className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1 disabled:opacity-50">
            <Crosshair size={11} weight="bold" /> Scroll to me
          </button>
          <button
            onClick={() => load(focus)}
            disabled={loading || !focus}
            data-testid="hierarchy-refresh"
            className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1">
            <ArrowsClockwise size={11} weight="bold" />
            {loading ? "Loading…" : "Refresh"}
          </button>

          <div className="ml-3 flex items-center gap-1.5 text-[11px] text-slate-700">
            <span className="uppercase font-bold tracking-wider text-[10px] text-slate-500">Down</span>
            <select value={downDepth} onChange={(e) => setDownDepth(+e.target.value)}
                    data-testid="hierarchy-down-depth"
                    className="border border-slate-300 rounded-sm px-1 py-0.5 text-[11px]">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span className="uppercase font-bold tracking-wider text-[10px] text-slate-500 ml-2">Up</span>
            <select value={upDepth} onChange={(e) => setUpDepth(+e.target.value)}
                    data-testid="hierarchy-up-depth"
                    className="border border-slate-300 rounded-sm px-1 py-0.5 text-[11px]">
              {[0, 1, 2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div className="ml-auto flex items-center gap-1">
            <button onClick={() => setZoom((z) => Math.max(0.4, z - 0.1))}
                    data-testid="hierarchy-zoom-out"
                    className="p-1.5 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700">
              <MagnifyingGlassMinus size={12} weight="bold" />
            </button>
            <span className="text-[11px] font-mono text-slate-600 w-9 text-center"
                  data-testid="hierarchy-zoom-value">
              {Math.round(zoom * 100)}%
            </span>
            <button onClick={() => setZoom((z) => Math.min(2, z + 0.1))}
                    data-testid="hierarchy-zoom-in"
                    className="p-1.5 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700">
              <MagnifyingGlassPlus size={12} weight="bold" />
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="text-[10.5px] text-slate-600 flex flex-wrap gap-x-4 gap-y-1 px-1">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm border-2 border-red-600 bg-red-50" />
            You
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm border border-violet-600 bg-violet-50" />
            Dealer / distributor
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm border border-slate-500 bg-white" />
            End-user / other
          </span>
          <span className="text-slate-500">Drag to pan · click any node to recenter · hover to highlight chain.</span>
        </div>

        {/* Error */}
        {err && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-sm text-[12px] text-red-800 inline-flex items-center gap-2"
               data-testid="hierarchy-error">
            <WarningCircle size={14} weight="bold" /> {err}
          </div>
        )}

        {/* Chart */}
        <div
          ref={scrollRef}
          data-testid="hierarchy-canvas"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="bg-white border border-slate-200 rounded-sm overflow-auto select-none"
          style={{ maxHeight: "62vh", cursor: "grab", touchAction: "none" }}>
          {loading && !data && (
            <div className="p-8 text-center text-[12px] text-slate-500">Loading…</div>
          )}
          {!loading && data && layout && (
            <div style={{ transform: `scale(${zoom})`, transformOrigin: "top left",
                          width: layout.totalWidth, height: layout.totalHeight }}>
              <HierarchyChart
                layout={layout}
                nodes={data.nodes || {}}
                truncated={data.truncated_children || {}}
                focus={data.center}
                onSelect={handleSelect}
                hoverId={hoverId}
                setHoverId={setHoverId}
              />
            </div>
          )}
        </div>

        {/* Details panel */}
        {selectedNode && (
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="hierarchy-details">
            <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
              <Buildings size={12} weight="bold" className="text-indigo-700" />
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
                Centered: {selectedNode.name || selectedNode.id}
              </h2>
              <button
                onClick={() => navigate(`/customers?search=${encodeURIComponent(selectedNode.id)}`)}
                data-testid="hierarchy-open-customer"
                className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1">
                <ArrowSquareOut size={10} weight="bold" /> Open in Customers
              </button>
            </div>
            <div className="px-3 py-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
              <Field label="CustomerID" value={selectedNode.id} mono />
              <Field label="Type" value={selectedNode.type || "—"} />
              <Field label="Country" value={selectedNode.country || "—"} />
              <Field label="Parent" value={selectedNode.parent_id || "(root)"} mono />
              <Field
                label="Ancestors shown"
                value={data?.ancestors?.length ?? 0}
              />
              <Field
                label="Descendants shown"
                value={(data?.descendants_by_level || [])
                  .reduce((a, l) => a + l.length, 0)}
              />
              <Field
                label="Truncated children"
                value={Object.values(data?.truncated_children || {})
                  .reduce((a, n) => a + (n || 0), 0)}
              />
              <Field label="Compressor owner" value={selectedNode.compressor_owner ? "Yes" : "No"} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className={`text-[12px] text-slate-900 ${mono ? "font-mono" : ""} truncate`}
           title={String(value)}>
        {value}
      </div>
    </div>
  );
}
